import { ArticleService, CroquisKind, SharedProps } from '@/types'
import { formatVatRate } from '@/utils/format'
import { Head, Link, router } from '@inertiajs/react'
import { ActionIcon, Button, Group, Menu, Paper, Select, Stack, Table, Text, TextInput, Title } from '@mantine/core'
import {
  IconChevronDown,
  IconChevronUp,
  IconDotsVertical,
  IconPencil,
  IconPlus,
  IconSearch,
  IconTrash,
} from '@tabler/icons-react'
import { useMemo, useState } from 'react'

interface ArticleServicesPageProps extends SharedProps {
  article_services: ArticleService[]
}

const CROQUIS_OPTIONS: { value: CroquisKind; label: string }[] = [
  { value: 'none', label: 'Aucun' },
  { value: 'garment', label: 'Vêtement' },
  { value: 'jacket', label: 'Bluson' },
  { value: 'shirt', label: 'Chemise' },
  { value: 'pants', label: 'Pantalon' },
  { value: 'jumpsuit', label: 'Combinaison' },
  { value: 'purse', label: 'Sac' },
  { value: 'wallet', label: 'Portefeuille' },
  { value: 'belt', label: 'Ceinture' },
  { value: 'hat', label: 'Casquette' },
  { value: 'shoes', label: 'Chaussures' },
  { value: 'sneakers', label: 'Baskets' },
  { value: 'ballerinas', label: 'Ballerines' },
  { value: 'heels', label: 'Talons' },
  { value: 'boots', label: 'Bottes' },
]

export default function ArticleServicesIndex({ article_services, current_organization }: ArticleServicesPageProps) {
  const [searchQuery, setSearchQuery] = useState('')

  const groupedServices = useMemo(() => {
    const groups: Record<string, ArticleService[]> = {}

    const filtered = !searchQuery.trim()
      ? article_services
      : article_services.filter(
          (as) =>
            as.article_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            as.service_name?.toLowerCase().includes(searchQuery.toLowerCase())
        )

    filtered.forEach((as) => {
      const name = as.article_name || 'Sans nom'
      if (!groups[name]) groups[name] = []
      groups[name].push(as)
    })

    return groups
  }, [article_services, searchQuery])

  const handleDelete = (as: ArticleService) => {
    if (confirm(`Êtes-vous sûr de vouloir supprimer le service ?`)) {
      router.delete(`/organizations/${current_organization?.slug}/article_services/${as.id}`)
    }
  }

  const handleReorder = (articleName: string, id: number, direction: 'up' | 'down') => {
    const services = groupedServices[articleName]
    const index = services.findIndex((s) => s.id === id)
    if (index === -1) return

    if (direction === 'up' && index === 0) return
    if (direction === 'down' && index === services.length - 1) return

    const newServices = [...services]
    const swapIndex = direction === 'up' ? index - 1 : index + 1
    ;[newServices[index], newServices[swapIndex]] = [newServices[swapIndex], newServices[index]]

    router.post(
      `/organizations/${current_organization?.slug}/article_services/reorder`,
      {
        ids: newServices.map((s) => s.id),
      },
      {
        preserveScroll: true,
      }
    )
  }

  const handleCroquisChange = (articleName: string, value: string | null) => {
    if (!value) return
    router.post(
      `/organizations/${current_organization?.slug}/article_services/set_croquis_kind`,
      { article_name: articleName, croquis_kind: value },
      { preserveScroll: true }
    )
  }

  return (
    <>
      <Head title={`Catalogue - ${current_organization?.name}`} />

      <Stack>
        <Group justify="space-between" align="center">
          <Title>Catalogue</Title>
          <Button
            component={Link}
            href={`/organizations/${current_organization?.slug}/article_services/new`}
            leftSection={<IconPlus size={18} />}
          >
            Nouveau service
          </Button>
        </Group>

        <TextInput
          placeholder="Rechercher par article ou service..."
          leftSection={<IconSearch size={16} />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />

        {Object.entries(groupedServices).map(([articleName, services]) => (
          <Stack key={articleName} gap="xs">
            <Group justify="space-between" mt="md" align="flex-end">
              <Text fw={700} size="lg">
                {articleName}
              </Text>
              <Select
                label="Croquis"
                data={CROQUIS_OPTIONS}
                value={services[0].croquis_kind}
                onChange={(value) => handleCroquisChange(articleName, value)}
                allowDeselect={false}
                w={180}
              />
            </Group>
            <Paper withBorder radius="md">
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ width: 100 }}>Ordre</Table.Th>
                    <Table.Th>Service</Table.Th>
                    <Table.Th>Prix</Table.Th>
                    <Table.Th>Prix société HT</Table.Th>
                    <Table.Th>TVA</Table.Th>
                    <Table.Th style={{ width: 40 }} />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {services.map((as, index) => (
                    <Table.Tr key={as.id}>
                      <Table.Td>
                        <Group gap={4}>
                          <ActionIcon
                            size="sm"
                            variant="subtle"
                            disabled={index === 0}
                            onClick={() => handleReorder(articleName, as.id, 'up')}
                          >
                            <IconChevronUp size={14} />
                          </ActionIcon>
                          <ActionIcon
                            size="sm"
                            variant="subtle"
                            disabled={index === services.length - 1}
                            onClick={() => handleReorder(articleName, as.id, 'down')}
                          >
                            <IconChevronDown size={14} />
                          </ActionIcon>
                        </Group>
                      </Table.Td>
                      <Table.Td
                        onClick={() =>
                          router.get(`/organizations/${current_organization?.slug}/article_services/${as.id}/edit`)
                        }
                        style={{ cursor: 'pointer' }}
                      >
                        {as.service_name}
                      </Table.Td>
                      <Table.Td>{Number(as.price).toFixed(2)}€</Table.Td>
                      <Table.Td>{Number(as.contract_price).toFixed(2)}€</Table.Td>
                      <Table.Td>{formatVatRate(as.vat_rate)}</Table.Td>
                      <Table.Td>
                        <Menu shadow="md" width={200} position="bottom-end">
                          <Menu.Target>
                            <ActionIcon variant="subtle" color="gray">
                              <IconDotsVertical size={16} />
                            </ActionIcon>
                          </Menu.Target>

                          <Menu.Dropdown>
                            <Menu.Item
                              leftSection={<IconPencil size={14} />}
                              onClick={() =>
                                router.get(
                                  `/organizations/${current_organization?.slug}/article_services/${as.id}/edit`
                                )
                              }
                            >
                              Modifier
                            </Menu.Item>
                            <Menu.Item
                              color="red"
                              leftSection={<IconTrash size={14} />}
                              onClick={() => handleDelete(as)}
                            >
                              Supprimer
                            </Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Paper>
          </Stack>
        ))}
      </Stack>
    </>
  )
}
